import classNames from 'classnames';
import Hammer from 'hammerjs';
import { prefix } from 'inline-style-prefixer';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { imagePropTypes } from '../../../propTypes';
import { above, between } from '../../../util/mediaQuery';

import Icon from '../Atoms/Icon';
import Picture from '../Picture';

const propTypes = exact({
  images: PropTypes.arrayOf(imagePropTypes).isRequired,
  title: PropTypes.string,
});

const defaultProps = {
  title: 'Commercial partners',
};

class SponsorCarousel extends Component {
  constructor(props) {
    super(props);

    this.animateCarousel = this.animateCarousel.bind(this);
    this.setGroupCount = this.setGroupCount.bind(this);

    this.carousel = undefined;
    this.handleWindowResize = undefined;
    this.touchManager = undefined;

    this.state = {
      activeGroup: 1,
      groupCount: this.getGroupCount(),
    };
  }

  componentDidMount() {
    this.handleWindowResize = debounce(this.setGroupCount, 1000 / 30);
    window.addEventListener('resize', this.handleWindowResize);
    this.addTouchSwipe();
  }

  componentWillUnmount() {
    if (this.handleWindowResize) {
      window.removeEventListener('resize', this.handleWindowResize);
    }

    if (this.touchManager) {
      this.touchManager.off('swipeleft');
      this.touchManager.off('swiperight');
    }
  }

  getGroupCount() {
    const imageCount = this.props.images.length;

    let groupCount = imageCount;
    if (between('mobileLarge', 'tablet')) groupCount = Math.ceil(imageCount / 2);
    if (above('tablet')) groupCount = Math.ceil(imageCount / 3);

    return groupCount;
  }

  setGroupCount() {
    const groupCount = this.getGroupCount();

    if (groupCount !== this.state.groupCount) {
      this.setState({
        groupCount,
        activeGroup: 1,
      });
    }
  }

  addTouchSwipe() {
    if (!this.carousel) return;
    this.touchManager = new Hammer.Manager(this.carousel, { touchAction: 'pan-y' });
    const swipe = new Hammer.Swipe();

    this.touchManager.add(swipe);
    this.touchManager.on('swipeleft', () => this.animateCarousel(1));
    this.touchManager.on('swiperight', () => this.animateCarousel(-1));
  }

  animateCarousel(increment) {
    const activeGroup = this.state.activeGroup + increment;
    if (activeGroup < 1 || activeGroup > this.state.groupCount) return;
    this.setState({ activeGroup });
  }

  render() {
    const translateX = (this.state.activeGroup - 1) * -100;

    const sliderStyles = prefix({
      transform: `translate3d(${translateX}%, 0, 0)`,
    });

    const sliderClasses = classNames('SponsorCarousel_slider', {
      'SponsorCarousel_slider-center': this.state.groupCount === 1,
    });

    return (
      <section className="SponsorCarousel">
        <div className="SponsorCarousel_container">
          <div className="SponsorCarousel_content">
            <h2 className="SponsorCarousel_title">{this.props.title}</h2>
            <div className="SponsorCarousel_carousel">
              {this.state.activeGroup > 1 && (
                <button
                  type="button"
                  onClick={() => this.animateCarousel(-1)}
                  className="SponsorCarousel_arrow SponsorCarousel_arrow-left"
                >
                  <Icon id="arrow-left" />
                </button>
              )}
              {this.state.activeGroup < this.state.groupCount && (
                <button
                  type="button"
                  onClick={() => this.animateCarousel(1)}
                  className="SponsorCarousel_arrow SponsorCarousel_arrow-right"
                >
                  <Icon id="arrow-right" />
                </button>
              )}
              <div
                className="SponsorCarousel_sliderWrapper"
                ref={(element) => {
                  this.carousel = element;
                }}
              >
                <div className={sliderClasses} style={sliderStyles}>
                  {this.props.images.map((image) => (
                    <div key={`${image.alt}-${image.url}`} className="SponsorCarousel_item">
                      <div className="SponsorCarousel_itemContent">
                        <Picture
                          {...image}
                          className="SponsorCarousel_image"
                          sizes={[
                            [100], // from 0
                            [100], // from 440
                            [80], // from 768
                            [60], // from 1024
                            ['880'], // from 1440
                          ]}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

SponsorCarousel.propTypes = propTypes;
SponsorCarousel.defaultProps = defaultProps;

export default SponsorCarousel;
